/*
================================================================================
| FILENAME: src/features/compass/CompassView.jsx (Upgraded & Fixed)
| PURPOSE: Adds a new button and logic to trigger the asset summarization
|          workflow for each product. This version is self-contained to fix
|          compilation errors by including necessary components and services.
|
| KEY CHANGES:
| 1.  FIX: Resolved import errors by defining apiService, PageLoader, and Modal
|     components directly within this file, making it self-contained.
| 2.  A new "Generate Asset Summaries" button is added to each Product Card.
| 3.  The button shows a loading state and is disabled during processing.
| 4.  A new handler function calls the backend API to start the n8n workflow.
================================================================================
*/
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios'; // Assuming axios is a project dependency
import { Save, Plus, Trash2, Edit, X, User, Users, Target, MessageSquare, Briefcase, Globe, Image as ImageIcon, Upload, FileText, Tag, Building, Sparkles, Cog } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://martechai-backend.onrender.com';

// --- SELF-CONTAINED SERVICES & COMPONENTS TO PREVENT IMPORT ERRORS ---

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
});

const apiService = {
    fetchBrandHub: () => apiClient.get('/api/brand-hub').then(res => res.data),
    updateBrandHub: (updateData) => apiClient.put('/api/brand-hub', updateData).then(res => res.data),
    updateProduct: (productId, productData) => apiClient.put(`/api/brand-hub/products/${productId}`, productData).then(res => res.data),
    deleteProduct: (productId) => apiClient.delete(`/api/brand-hub/products/${productId}`).then(res => res.data),
    createCustomerProfile: (profileData) => apiClient.post('/api/brand-hub/customer-profiles', profileData).then(res => res.data),
    updateCustomerProfile: (profileId, profileData) => apiClient.put(`/api/brand-hub/customer-profiles/${profileId}`, profileData).then(res => res.data),
    deleteCustomerProfile: (profileId) => apiClient.delete(`/api/brand-hub/customer-profiles/${profileId}`).then(res => res.data),
    uploadProductLogo: (productId, file) => {
        const formData = new FormData();
        formData.append('logo', file);
        return apiClient.post(`/api/brand-hub/assets/product/${productId}/logo`, formData).then(res => res.data);
    },
    uploadBrandAsset: (file) => {
        const formData = new FormData();
        formData.append('asset', file);
        return apiClient.post('/api/brand-hub/assets/brand', formData).then(res => res.data);
    },
    deleteBrandAsset: (assetId) => apiClient.delete(`/api/brand-hub/assets/brand/${assetId}`).then(res => res.data),
    uploadProductAsset: (productId, file) => {
        const formData = new FormData();
        formData.append('asset', file);
        return apiClient.post(`/api/brand-hub/assets/product/${productId}`, formData).then(res => res.data);
    },
    deleteProductAsset: (productId, assetId) => apiClient.delete(`/api/brand-hub/assets/product/${productId}/${assetId}`).then(res => res.data),
    generateProductKeywords: (productId) => apiClient.post(`/api/brand-hub/products/${productId}/generate-keywords`).then(res => res.data),
    processProductAssets: (productId) => apiClient.post('/api/n8n/process-product-assets', { productId }).then(res => res.data),
};

const PageLoader = ({ text = "Loading..." }) => (
    <div className="flex flex-col items-center justify-center h-screen bg-slate-900 text-slate-300">
        <Cog size={48} className="animate-spin text-purple-500 mb-4" />
        <p className="text-lg">{text}</p>
    </div>
);

const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex justify-center items-center p-4">
            <div className="bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b border-slate-700">
                    <h3 className="text-xl font-bold text-slate-100">{title}</h3>
                    <button onClick={onClose} className="p-1 text-slate-400 hover:text-white"><X size={24} /></button>
                </div>
                <div className="p-6 overflow-y-auto">{children}</div>
            </div>
        </div>
    );
};


// --- Reusable Sub-Components ---
const EditableTextArea = ({ value, onSave, label, placeholder, icon }) => {
    const [text, setText] = useState(value || '');
    const [isEditing, setIsEditing] = useState(false);
    const handleSave = () => { onSave(text); setIsEditing(false); };
    return (
        <div>
            <label className="flex items-center gap-2 text-lg font-semibold text-slate-200 mb-2">{icon} {label}</label>
            {isEditing ? (
                <div>
                    <textarea value={text} onChange={(e) => setText(e.target.value)} className="w-full h-32 p-2 bg-slate-900 border border-slate-600 rounded-md" placeholder={placeholder} />
                    <div className="flex justify-end gap-2 mt-2">
                        <button onClick={() => setIsEditing(false)} className="btn-secondary px-4 py-1 text-sm">Cancel</button>
                        <button onClick={handleSave} className="btn-primary px-4 py-1 text-sm">Save</button>
                    </div>
                </div>
            ) : (
                <div onClick={() => setIsEditing(true)} className="p-4 bg-slate-800/50 rounded-md min-h-[8rem] cursor-pointer hover:bg-slate-700/50">
                    <p className="whitespace-pre-wrap text-slate-300">{value || <span className="text-slate-500">{placeholder}</span>}</p>
                </div>
            )}
        </div>
    );
};

const EditableTagList = ({ tags, onSave, label, placeholder, icon }) => {
    const [currentTags, setCurrentTags] = useState(tags || []);
    const [inputValue, setInputValue] = useState('');
    const handleAddTag = () => {
        if (inputValue && !currentTags.includes(inputValue)) {
            const newTags = [...currentTags, inputValue];
            setCurrentTags(newTags);
            onSave(newTags);
            setInputValue('');
        }
    };
    const handleRemoveTag = (tagToRemove) => {
        const newTags = currentTags.filter(tag => tag !== tagToRemove);
        setCurrentTags(newTags);
        onSave(newTags);
    };
    return (
        <div>
            <label className="flex items-center gap-2 text-lg font-semibold text-slate-200 mb-2">{icon} {label}</label>
            <div className="p-4 bg-slate-800/50 rounded-md">
                <div className="flex flex-wrap gap-2">
                    {currentTags.map((tag, index) => (
                        <span key={index} className="flex items-center gap-1 bg-purple-600/50 text-purple-300 px-3 py-1 rounded-full text-sm">
                            {tag} <button onClick={() => handleRemoveTag(tag)}><X size={14} /></button>
                        </span>
                    ))}
                </div>
                <div className="flex gap-2 mt-4">
                    <input type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} placeholder={placeholder} className="flex-grow bg-slate-900 border border-slate-600 rounded-md px-2 py-1 text-sm" />
                    <button onClick={handleAddTag} className="btn-secondary px-4 py-1 text-sm">Add</button>
                </div>
            </div>
        </div>
    );
};

const EditableKeywordList = ({ keywords, onSave }) => {
    const [currentKeywords, setCurrentKeywords] = useState(keywords || []);
    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
        setCurrentKeywords(keywords || []);
    }, [keywords]);

    const handleAddKeyword = () => {
        const newKeyword = inputValue.trim().toLowerCase();
        if (newKeyword && !currentKeywords.includes(newKeyword)) {
            const newKeywords = [...currentKeywords, newKeyword];
            setCurrentKeywords(newKeywords);
            onSave(newKeywords);
            setInputValue('');
        }
    };

    const handleRemoveKeyword = (keywordToRemove) => {
        const newKeywords = currentKeywords.filter(keyword => keyword !== keywordToRemove);
        setCurrentKeywords(newKeywords);
        onSave(newKeywords);
    };

    return (
        <div>
            <div className="flex flex-wrap gap-1 min-h-[20px]">
                {(currentKeywords).map((keyword, i) => (
                    <span key={i} className="flex items-center gap-1 bg-slate-700 text-slate-300 pl-2 pr-1 py-0.5 rounded text-xs">
                        {keyword}
                        <button onClick={() => handleRemoveKeyword(keyword)} className="hover:text-red-400"><X size={14} /></button>
                    </span>
                ))}
            </div>
            <div className="flex gap-2 mt-3">
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddKeyword())}
                    placeholder="Add a keyword..."
                    className="flex-grow bg-slate-900 border border-slate-600 rounded-md px-2 py-1 text-sm"
                />
                <button onClick={handleAddKeyword} className="btn-secondary px-3 py-1 text-sm">Add</button>
            </div>
        </div>
    );
};

const AssetManager = ({ assets, onAssetUpload, onAssetDelete, label, icon }) => {
    const fileInputRef = useRef(null);
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) onAssetUpload(file);
    };
    return (
        <div>
            <label className="flex items-center gap-2 text-lg font-semibold text-slate-200 mb-2">{icon} {label}</label>
            <div className="p-4 bg-slate-800/50 rounded-md">
                <div className="space-y-2">
                    {(assets || []).map(asset => (
                        <div key={asset._id} className="flex items-center justify-between bg-slate-700/50 p-2 rounded-md">
                            <a href={`${API_BASE_URL}/api/brand-hub/assets/${asset.path}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-slate-300 hover:text-purple-400">
                                <FileText size={16} /> {asset.name}
                            </a>
                            <button onClick={() => onAssetDelete(asset._id)} className="p-1 text-slate-400 hover:text-red-400"><Trash2 size={14} /></button>
                        </div>
                    ))}
                </div>
                <button onClick={() => fileInputRef.current.click()} className="w-full mt-4 btn-secondary py-2 text-sm flex items-center justify-center gap-2">
                    <Upload size={14} /> Upload New Asset
                </button>
                <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
            </div>
        </div>
    );
};

const CustomerProfileManager = ({ profiles, onUpdate }) => {
    const [editingProfile, setEditingProfile] = useState(null);

    const handleSave = async (profileData) => {
        try {
            let updatedHub;
            if (profileData._id) {
                updatedHub = await apiService.updateCustomerProfile(profileData._id, profileData);
            } else {
                updatedHub = await apiService.createCustomerProfile(profileData);
            }
            onUpdate(updatedHub);
            setEditingProfile(null);
        } catch (error) {
            alert(`Error saving profile: ${error.message}`);
        }
    };

    const handleDelete = async (profileId) => {
        if (window.confirm("Are you sure you want to delete this customer profile?")) {
            try {
                const updatedHub = await apiService.deleteCustomerProfile(profileId);
                onUpdate(updatedHub);
            } catch (error) {
                alert(`Error deleting profile: ${error.message}`);
            }
        }
    };

    return (
        <div className="card p-6">
            <h3 className="flex items-center gap-2 text-xl font-bold text-main mb-4"><Users /> Ideal Customer Profiles</h3>
            <div className="space-y-4">
                {(profiles || []).map(profile => (
                    <div key={profile._id} className="p-4 bg-slate-800/50 rounded-md flex justify-between items-start">
                        <div>
                            <p className="font-semibold text-slate-200">{profile.name}</p>
                            <p className="text-sm text-slate-400 mt-1">{profile.description ? profile.description.substring(0, 50) : profile.demographics.substring(0, 50)}...</p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => setEditingProfile(profile)} className="p-2 text-slate-400 hover:text-purple-400"><Edit size={16} /></button>
                            <button onClick={() => handleDelete(profile._id)} className="p-2 text-slate-400 hover:text-red-400"><Trash2 size={16} /></button>
                        </div>
                    </div>
                ))}
            </div>
            <button onClick={() => setEditingProfile({ name: '', titles: '', demographics: '', painPoints: '', goals: ''})} className="w-full mt-4 btn-secondary py-2 flex items-center justify-center gap-2">
                <Plus size={16} /> Add Customer Profile
            </button>

            {editingProfile && (
                <Modal isOpen={true} onClose={() => setEditingProfile(null)} title={editingProfile._id ? "Edit Customer Profile" : "Create Customer Profile"}>
                    <CustomerProfileForm profile={editingProfile} onSave={handleSave} onCancel={() => setEditingProfile(null)} />
                </Modal>
            )}
        </div>
    );
};

const CustomerProfileForm = ({ profile, onSave, onCancel }) => {
    const [formData, setFormData] = useState(profile);
    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };
    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-slate-300">Profile Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md" />
            </div>
            <div>
                <label className="block text-sm font-medium text-slate-300">Relevant Job Titles (comma-separated)</label>
                <input type="text" name="titles" value={formData.titles} onChange={handleChange} className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md" placeholder="e.g., CEO, Marketing Manager" />
            </div>
            <div>
                <label className="block text-sm font-medium text-slate-300">Demographics</label>
                <textarea name="demographics" value={formData.demographics} onChange={handleChange} className="w-full h-24 p-2 bg-slate-700 border border-slate-600 rounded-md" />
            </div>
            <div>
                <label className="block text-sm font-medium text-slate-300">Pain Points</label>
                <textarea name="painPoints" value={formData.painPoints} onChange={handleChange} className="w-full h-24 p-2 bg-slate-700 border border-slate-600 rounded-md" />
            </div>
            <div>
                <label className="block text-sm font-medium text-slate-300">Goals</label>
                <textarea name="goals" value={formData.goals} onChange={handleChange} className="w-full h-24 p-2 bg-slate-700 border border-slate-600 rounded-md" />
            </div>
            <div className="flex justify-end gap-2 pt-4">
                <button type="button" onClick={onCancel} className="btn-secondary px-4 py-2">Cancel</button>
                <button type="submit" className="btn-primary px-4 py-2">Save Profile</button>
            </div>
        </form>
    );
};

const ProductEditorModal = ({ product, customerProfiles, onSave, onCancel, onDelete }) => {
    const [formData, setFormData] = useState(product);
    
    useEffect(() => {
        setFormData(product);
    }, [product]);

    const handleChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
    const handleCompetitorsSave = (newCompetitors) => setFormData(p => ({ ...p, productCompetitors: newCompetitors }));
    const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };

    return (
        <Modal isOpen={true} onClose={onCancel} title={`Editing: ${product.title}`}>
            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-slate-300">Product Name</label>
                    <input type="text" name="title" value={formData.title} onChange={handleChange} className="w-full p-2 bg-slate-700 border border-slate-600 rounded-md" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-300">Description</label>
                    <textarea name="description" value={formData.description} onChange={handleChange} className="w-full h-24 p-2 bg-slate-700 border border-slate-600 rounded-md" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-slate-300">Target Customer Profile</label>
                    <select name="customerProfile" value={formData.customerProfile || ''} onChange={handleChange} className="w-full mt-1 p-2 bg-slate-700 border border-slate-600 rounded-md text-sm">
                        <option value="">None</option>
                        {(customerProfiles || []).map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                    </select>
                </div>
                <EditableTagList tags={formData.productCompetitors} onSave={handleCompetitorsSave} label="Product Competitors" placeholder="Add a competitor..." icon={<Users size={16} />} />
                <div className="flex justify-between items-center pt-4">
                    <button type="button" onClick={onDelete} className="text-red-500 hover:text-red-400 text-sm font-semibold flex items-center gap-1">
                        <Trash2 size={14} /> Delete Product
                    </button>
                    <div className="flex gap-2">
                        <button type="button" onClick={onCancel} className="btn-secondary px-4 py-2">Cancel</button>
                        <button type="submit" className="btn-primary px-4 py-2">Save Product</button>
                    </div>
                </div>
            </form>
        </Modal>
    );
};


// --- Main View Component ---
export default function CompassView() {
    const [brandHub, setBrandHub] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('products');
    const [editingProduct, setEditingProduct] = useState(null);
    const [generatingKeywordsFor, setGeneratingKeywordsFor] = useState(null);
    const [processingAssetsFor, setProcessingAssetsFor] = useState(null);

    const fetchBrandHub = useCallback(async () => {
        try {
            const data = await apiService.fetchBrandHub();
            setBrandHub(data);
        } catch (error) {
            console.error("Failed to fetch Brand Hub:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchBrandHub(); }, [fetchBrandHub]);
    
    const handleUpdate = (updatedHub) => setBrandHub(updatedHub);

    const handleSaveField = async (field, value) => {
        try {
            const updatedHub = await apiService.updateBrandHub({ [field]: value });
            setBrandHub(updatedHub);
        } catch (error) { alert(`Error saving field: ${error.message}`); }
    };
    
    const handleAddProduct = async () => {
        const currentProducts = brandHub?.products || [];
        const updatedProducts = [...currentProducts, { title: 'New Product', description: 'A brief description.' }];
        handleSaveField('products', updatedProducts);
    };
    
    const handleProductUpdate = async (productData) => {
        try {
            const updatedHub = await apiService.updateProduct(productData._id, productData);
            setBrandHub(updatedHub);
            setEditingProduct(null);
        } catch (error) { alert(`Error updating product: ${error.message}`); }
    };

    const handleDeleteProduct = async (productId) => {
        if (window.confirm("Are you sure you want to permanently delete this product and all its assets?")) {
            try {
                const updatedHub = await apiService.deleteProduct(productId);
                setBrandHub(updatedHub);
                setEditingProduct(null);
            } catch (error) {
                alert(`Error deleting product: ${error.message}`);
            }
        }
    };
    
    const handleLogoUpload = async (productId, file) => {
        try {
            const updatedHub = await apiService.uploadProductLogo(productId, file);
            setBrandHub(updatedHub);
        } catch (error) { alert(`Error uploading logo: ${error.message}`); }
    };
    
    const handleBrandAssetUpload = async (file) => {
        try {
            const updatedHub = await apiService.uploadBrandAsset(file);
            setBrandHub(updatedHub);
        } catch (error) { alert(`Error uploading brand asset: ${error.message}`); }
    };

    const handleBrandAssetDelete = async (assetId) => {
        try {
            const updatedHub = await apiService.deleteBrandAsset(assetId);
            setBrandHub(updatedHub);
        } catch (error) { alert(`Error deleting brand asset: ${error.message}`); }
    };

    const handleProductAssetUpload = async (productId, file) => {
        try {
            const updatedHub = await apiService.uploadProductAsset(productId, file);
            setBrandHub(updatedHub);
        } catch (error) {
            alert(`Error uploading product asset: ${error.message}`);
        }
    };
    
    const handleProductAssetDelete = async (productId, assetId) => {
        try {
            const updatedHub = await apiService.deleteProductAsset(productId, assetId);
            setBrandHub(updatedHub);
        } catch (error) {
            alert(`Error deleting product asset: ${error.message}`);
        }
    };

    const handleGenerateKeywords = async (productId) => {
        setGeneratingKeywordsFor(productId);
        try {
            const updatedHub = await apiService.generateProductKeywords(productId);
            setBrandHub(updatedHub);
        } catch (error) {
            alert(`Error generating keywords: ${error.message}`);
        } finally {
            setGeneratingKeywordsFor(null);
        }
    };

    const handleKeywordsSave = async (productId, newKeywords) => {
        try {
            const updatedHub = await apiService.updateProduct(productId, { productKeywords: newKeywords });
            setBrandHub(updatedHub);
        } catch (error) {
            alert(`Error saving keywords: ${error.message}`);
        }
    };

    const handleProcessAssets = async (productId) => {
        setProcessingAssetsFor(productId);
        try {
            const result = await apiService.processProductAssets(productId);
            alert(result.message || 'Asset processing has been initiated. Summaries will appear in the Data Hub shortly.');
        } catch (error) {
            alert(`Error starting asset processing: ${error.response?.data?.message || error.message}`);
        } finally {
            setProcessingAssetsFor(null);
        }
    };

    if (isLoading) return <PageLoader text="Loading Compass..." />;
    if (!brandHub) return <div>Error loading Brand Hub data.</div>;

    const TabButton = ({ id, label, icon }) => (
        <button onClick={() => setActiveTab(id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors ${activeTab === id ? 'bg-purple-600/20 text-purple-300' : 'text-slate-400 hover:bg-slate-700/50'}`}>
            {icon} {label}
        </button>
    );
    
    const ProductCard = ({ product, isGenerating, isProcessingAssets }) => {
        const logoFileInputRef = useRef(null);
        const assetFileInputRef = useRef(null);
        
        const handleLogoFileChange = (e) => {
            const file = e.target.files[0];
            if (file) handleLogoUpload(product._id, file);
        };

        const handleAssetFileChange = (e) => {
            const file = e.target.files[0];
            if (file) handleProductAssetUpload(product._id, file);
        };

        return (
            <div className="card p-4 flex flex-col bg-slate-800/50 rounded-lg">
                <div className="flex items-start gap-4">
                    <div className="relative w-20 h-20 flex-shrink-0 group">
                        <img 
                            src={product.productLogo ? `${API_BASE_URL}/api/brand-hub/assets/${product.productLogo}` : 'https://placehold.co/80x80/1E293B/94A3B8?text=Logo'} 
                            alt={`${product.title} logo`} 
                            className="w-full h-full object-cover rounded-md" 
                        />
                        <button onClick={() => logoFileInputRef.current.click()} className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-md">
                            <Upload size={24} className="text-white" />
                        </button>
                        <input type="file" ref={logoFileInputRef} onChange={handleLogoFileChange} accept="image/*" className="hidden" />
                    </div>
                    <div className="flex-grow">
                        <h4 className="font-bold text-main text-slate-100">{product.title}</h4>
                        <p className="text-sm text-slate-400 line-clamp-2">{product.description}</p>
                    </div>
                </div>
                
                <div className="mt-4 border-t border-slate-700 pt-3">
                    <label className="text-xs font-semibold text-slate-400">Product Assets</label>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {(product.productAssets || []).map(asset => (
                            <a key={asset._id} href={`${API_BASE_URL}/api/brand-hub/assets/${asset.path}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 bg-slate-700 text-slate-300 px-2 py-1 rounded-md text-xs border border-slate-600 hover:bg-slate-600">
                                <FileText size={14} /> {asset.name}
                                <button onClick={(e) => { e.preventDefault(); handleProductAssetDelete(product._id, asset._id); }} className="ml-1 hover:text-red-400"><X size={12} /></button>
                            </a>
                        ))}
                        <button onClick={() => assetFileInputRef.current.click()} className="text-xs bg-purple-600/20 text-purple-300 hover:bg-purple-600/40 px-2 py-1 rounded-md">
                            <Plus size={14} />
                        </button>
                        <input type="file" ref={assetFileInputRef} onChange={handleAssetFileChange} className="hidden" />
                    </div>
                </div>

                <div className="mt-4 border-t border-slate-700 pt-3">
                    <div className="flex justify-between items-center mb-2">
                        <label className="text-xs font-semibold text-slate-400">Relevant Keywords</label>
                        <button 
                            onClick={() => handleGenerateKeywords(product._id)} 
                            disabled={isGenerating}
                            className="btn-secondary text-xs px-2 py-1 disabled:opacity-50 disabled:cursor-wait flex items-center gap-1"
                        >
                            {isGenerating ? 'Analyzing...' : <><Sparkles size={12}/> Generate with AI</>}
                        </button>
                    </div>
                    <EditableKeywordList
                        keywords={product.productKeywords}
                        onSave={(newKeywords) => handleKeywordsSave(product._id, newKeywords)}
                    />
                </div>

                <div className="mt-auto pt-4 space-y-2">
                     <button 
                        onClick={() => handleProcessAssets(product._id)}
                        disabled={isProcessingAssets || !product.productAssets || product.productAssets.length === 0}
                        className="w-full btn-primary py-1 text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isProcessingAssets ? <><Cog size={14} className="animate-spin" /> Processing Assets...</> : <><Sparkles size={14} /> Generate Asset Summaries</>}
                    </button>
                    <button onClick={() => setEditingProduct(product)} className="w-full btn-secondary py-1 text-sm">Edit Details</button>
                </div>
            </div>
        );
    };

    return (
        <div className="p-4 sm:p-6 lg:p-8">
            <h1 className="text-4xl font-bold text-main text-slate-100 mb-2">Brand Compass</h1>
            <p className="text-lg text-slate-400 mb-8">Define your brand's core identity to guide all marketing efforts.</p>

            <div className="flex items-center gap-2 border-b border-slate-700 mb-8">
                <TabButton id="identity" label="Core Identity" icon={<Target size={18} />} />
                <TabButton id="profiles" label="Customer Profiles" icon={<Users size={18} />} />
                <TabButton id="products" label="Products & Services" icon={<Briefcase size={18} />} />
            </div>

            {activeTab === 'identity' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <EditableTextArea label="Mission Statement" icon={<Target />} value={brandHub.mission} onSave={(val) => handleSaveField('mission', val)} placeholder="What is your company's core purpose?" />
                    <EditableTextArea label="Brand Voice & Tone" icon={<MessageSquare />} value={brandHub.brandVoice} onSave={(val) => handleSaveField('brandVoice', val)} placeholder="Describe your brand's personality (e.g., professional, witty, empathetic)." />
                    <EditableTagList tags={brandHub.companyCompetitors} onSave={(val) => handleSaveField('companyCompetitors', val)} label="Company Competitors" placeholder="Add a competitor URL..." icon={<Building size={18} />} />
                    <AssetManager assets={brandHub.brandAssets} onAssetUpload={handleBrandAssetUpload} onAssetDelete={handleBrandAssetDelete} label="Brand Assets & Guidelines" icon={<ImageIcon size={18} />} />
                </div>
            )}
            
            {activeTab === 'profiles' && (
                <CustomerProfileManager profiles={brandHub.customerProfiles || []} onUpdate={handleUpdate} />
            )}

            {activeTab === 'products' && (
                <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {(brandHub.products || []).map(product => (
                            <ProductCard 
                                key={product._id} 
                                product={product}
                                isGenerating={generatingKeywordsFor === product._id}
                                isProcessingAssets={processingAssetsFor === product._id}
                            />
                        ))}
                    </div>
                    <button onClick={handleAddProduct} className="w-full mt-6 btn-secondary py-3 flex items-center justify-center gap-2 border-2 border-dashed border-slate-600 hover:border-purple-500">
                        <Plus size={16} /> Add Product/Service
                    </button>
                </div>
            )}

            {editingProduct && (
                <ProductEditorModal 
                    product={editingProduct}
                    customerProfiles={brandHub.customerProfiles || []}
                    onSave={handleProductUpdate}
                    onCancel={() => setEditingProduct(null)}
                    onDelete={() => handleDeleteProduct(editingProduct._id)}
                />
            )}
        </div>
    );
}